/**
 * GraphQL PMS Service
 * Connects to AppSync API for Performance Management System operations.
 * Follows the same pattern as graphqlNomination.ts.
 * All query/mutation names match the deployed AppSync schema (PMS prefix).
 */

import { generateClient } from 'aws-amplify/api';
import {
  Objective,
  ObjectiveStatus,
  Milestone,
  ObjectiveVersion,
  ObjectiveEvaluationSummary,
  Review,
  ReviewType,
  ReviewCycle,
  ReviewCycleStatus,
  ReviewPhase,
  TrackBy,
  ChatMessage,
  ChatThreadType,
  Attachment,
  AttachmentParentType,
  RatingScale,
  CreateObjectiveInput,
  UpdateObjectiveInput,
  CreateMilestoneInput,
  CreateReviewCycleInput,
  CreateReviewInput,
  UpdateReviewInput,
  SubmitSelfReviewInput,
  ReviewWorkflowStatus,
  ObjectiveRating,
  ReviewHistoryAction,
  ReviewHistoryEntry,
  ReviewMetadata,
  CreateChatMessageInput,
  CreateAttachmentInput,
} from '../types/pms';
import { enrichObjectiveRatings, resolveCanonicalReview } from './pmsWorkflow';
import { collectAcrossIdentities, sameIdentity } from './identity';

// ---- Lazy Client Init ----

let client: any = null;
const getClient = () => {
  if (!client) client = generateClient();
  return client;
};

const getAuthMode = (): { authMode: 'userPool' } => ({ authMode: 'userPool' as const });
const getData = (result: any): any => ('data' in result ? result.data : null);
const PMS_REVIEW_CACHE_TTL_MS = 60000;
const pmsReviewCache = new Map<string, { timestamp: number; reviews: Review[] }>();
const pmsObjectiveCache = new Map<string, { timestamp: number; objectives: Objective[] }>();
const pmsCycleCache = new Map<string, { timestamp: number; cycles: ReviewCycle[] }>();
const graphqlExecutionCounts = new Map<string, number>();

function getOperationName(query: unknown): string {
  const text = String(query || '');
  const match = text.match(/\b(query|mutation|subscription)\s+([A-Za-z0-9_]+)/);
  return match?.[2] || 'AnonymousGraphQL';
}

function graphqlWithLog(request: any): Promise<any> {
  const operationName = getOperationName(request?.query);
  const source = 'GraphQLPMSService';
  const countKey = `${source}:${operationName}`;
  const executionCount = (graphqlExecutionCounts.get(countKey) || 0) + 1;
  graphqlExecutionCounts.set(countKey, executionCount);
  console.log('GRAPHQL:', operationName, { source, executionCount });
  return getClient().graphql(request) as Promise<any>;
}

function getCachedReviews(key: string): Review[] | null {
  const cached = pmsReviewCache.get(key);
  if (!cached || Date.now() - cached.timestamp > PMS_REVIEW_CACHE_TTL_MS) return null;
  return cached.reviews;
}

function setCachedReviews(key: string, reviews: Review[]): void {
  pmsReviewCache.set(key, { timestamp: Date.now(), reviews });
}

function getCachedObjectives(key: string): Objective[] | null {
  const cached = pmsObjectiveCache.get(key);
  if (!cached || Date.now() - cached.timestamp > PMS_REVIEW_CACHE_TTL_MS) return null;
  return cached.objectives;
}

function setCachedObjectives(key: string, objectives: Objective[]): void {
  pmsObjectiveCache.set(key, { timestamp: Date.now(), objectives });
}

function getCachedCycles(key: string): ReviewCycle[] | null {
  const cached = pmsCycleCache.get(key);
  if (!cached || Date.now() - cached.timestamp > PMS_REVIEW_CACHE_TTL_MS) return null;
  return cached.cycles;
}

function setCachedCycles(key: string, cycles: ReviewCycle[]): void {
  pmsCycleCache.set(key, { timestamp: Date.now(), cycles });
}

function clearPmsReadCaches(): void {
  pmsReviewCache.clear();
  pmsObjectiveCache.clear();
  pmsCycleCache.clear();
}

// ---- Objective field fragment (reused across queries) ----
const OBJ_FIELDS = `id employeeId employeeName reviewCycleId title description measureOfSuccess
      startDate endDate weightage progressPercentage status
      finalProgressPercentage managerFinalComment evaluationSummary
      employeeComments managerComments managerId managerName isLocked trackBy createdAt updatedAt`;

const OBJ_APPROVAL_LIST_FIELDS = `id employeeId employeeName reviewCycleId title description
      weightage progressPercentage status managerComments managerId managerName createdAt updatedAt`;

const MS_FIELDS = `id objectiveId title description dueDate weightage progressPercentage createdAt updatedAt`;

const REVIEW_FIELDS = `id employeeId employeeName reviewCycleId reviewType quarter
      objectiveRatings overallRating suggestedRating overallComments developmentSuggestions
      reviewerId reviewerName workflowStatus isAcknowledged acknowledgedAt acknowledgedBy reviewMetadata
      employeeRating employeeRatingDescription employeeComment employeeSubmittedAt managerSubmittedAt
      timestamp createdAt updatedAt`;

const PMS_TABLES = {
  objectives: 'PMS_Objectives',
  reviews: 'PMS_Final_Reviews',
  messages: 'PMS_Chat_Messages',
  attachments: 'PMS_Attachments',
} as const;

function logPmsRead(details: {
  operation: string;
  table: string;
  reviewId?: string;
  reviewCycleId?: string;
  employeeId?: string;
  recordCount: number;
}): void {
  console.log('[GraphQL PMS][ReadTrace]', details);
}

const CYCLE_FIELDS = `id name reviewCycleType startDate endDate midYearDeadline endYearDeadline submissionDeadline
      status isLocked annualCycleId reviewPhase createdAt updatedAt`;

// ---- GraphQL Query Strings (PMS-prefixed to match AppSync schema) ----

const listObjectivesQuery = /* GraphQL */ `
  query ListPMSObjectives($limit: Int, $nextToken: String) {
    listPMSObjectives(limit: $limit, nextToken: $nextToken) {
      items { ${OBJ_FIELDS} }
      nextToken
    }
  }
`;

const getObjectiveQuery = /* GraphQL */ `
  query GetPMSObjective($id: ID!) {
    getPMSObjective(id: $id) { ${OBJ_FIELDS} }
  }
`;

const objectivesByEmployeeQuery = /* GraphQL */ `
  query PMSObjectivesByEmployee($employeeId: String!, $limit: Int, $nextToken: String) {
    pmsObjectivesByEmployee(employeeId: $employeeId, limit: $limit, nextToken: $nextToken) {
      items { ${OBJ_FIELDS} }
      nextToken
    }
  }
`;

const objectivesByReviewCycleQuery = /* GraphQL */ `
  query PMSObjectivesByReviewCycle($reviewCycleId: String!, $limit: Int, $nextToken: String) {
    pmsObjectivesByReviewCycle(reviewCycleId: $reviewCycleId, limit: $limit, nextToken: $nextToken) {
      items { ${OBJ_FIELDS} }
      nextToken
    }
  }
`;

const objectivesByStatusQuery = /* GraphQL */ `
  query PMSObjectivesByStatus($status: String!, $limit: Int, $nextToken: String) {
    pmsObjectivesByStatus(status: $status, limit: $limit, nextToken: $nextToken) {
      items { ${OBJ_FIELDS} }
      nextToken
    }
  }
`;

const pendingApprovalObjectivesByStatusQuery = /* GraphQL */ `
  query PMSPendingApprovalObjectivesByStatus($status: String!, $limit: Int, $nextToken: String) {
    pmsObjectivesByStatus(status: $status, limit: $limit, nextToken: $nextToken) {
      items { ${OBJ_APPROVAL_LIST_FIELDS} }
      nextToken
    }
  }
`;

const objectivesByManagerQuery = /* GraphQL */ `
  query PMSObjectivesByManager($managerId: String!, $limit: Int, $nextToken: String) {
    pmsObjectivesByManager(managerId: $managerId, limit: $limit, nextToken: $nextToken) {
      items { ${OBJ_FIELDS} }
      nextToken
    }
  }
`;

const pendingApprovalObjectivesByManagerQuery = /* GraphQL */ `
  query PMSPendingApprovalObjectivesByManager($managerId: String!, $limit: Int, $nextToken: String) {
    pmsObjectivesByManager(managerId: $managerId, limit: $limit, nextToken: $nextToken) {
      items { ${OBJ_APPROVAL_LIST_FIELDS} }
      nextToken
    }
  }
`;

const listMilestonesQuery = /* GraphQL */ `
  query PMSMilestonesByObjective($objectiveId: String!, $limit: Int) {
    pmsMilestonesByObjective(objectiveId: $objectiveId, limit: $limit) {
      items { ${MS_FIELDS} }
    }
  }
`;

const getReviewQuery = /* GraphQL */ `
  query GetPMSReview($id: ID!) {
    getPMSReview(id: $id) { ${REVIEW_FIELDS} }
  }
`;

const reviewsByEmployeeQuery = /* GraphQL */ `
  query PMSReviewsByEmployee($employeeId: String!, $limit: Int, $nextToken: String) {
    pmsReviewsByEmployee(employeeId: $employeeId, limit: $limit, nextToken: $nextToken) {
      items { ${REVIEW_FIELDS} }
      nextToken
    }
  }
`;

const reviewsByReviewCycleQuery = /* GraphQL */ `
  query PMSReviewsByReviewCycle($reviewCycleId: String!, $limit: Int, $nextToken: String) {
    pmsReviewsByReviewCycle(reviewCycleId: $reviewCycleId, limit: $limit, nextToken: $nextToken) {
      items { ${REVIEW_FIELDS} }
      nextToken
    }
  }
`;

const listReviewCyclesQuery = /* GraphQL */ `
  query ListPMSReviewCycles($limit: Int, $nextToken: String) {
    listPMSReviewCycles(limit: $limit, nextToken: $nextToken) {
      items { ${CYCLE_FIELDS} }
      nextToken
    }
  }
`;

const reviewCyclesByStatusQuery = /* GraphQL */ `
  query PMSReviewCyclesByStatus($status: String!, $limit: Int, $nextToken: String) {
    pmsReviewCyclesByStatus(status: $status, limit: $limit, nextToken: $nextToken) {
      items { ${CYCLE_FIELDS} }
      nextToken
    }
  }
`;

const getReviewCycleQuery = /* GraphQL */ `
  query GetPMSReviewCycle($id: ID!) {
    getPMSReviewCycle(id: $id) { ${CYCLE_FIELDS} }
  }
`;

const chatMessagesByThreadQuery = /* GraphQL */ `
  query PMSChatMessagesByThread($threadId: String!, $limit: Int) {
    pmsChatMessagesByThread(threadId: $threadId, limit: $limit) {
      items {
        id threadType threadId senderId senderName content timestamp
      }
    }
  }
`;

const attachmentsByParentQuery = /* GraphQL */ `
  query PMSAttachmentsByParent($parentId: String!, $limit: Int) {
    pmsAttachmentsByParent(parentId: $parentId, limit: $limit) {
      items {
        id parentType parentId fileName fileType fileSize s3Key uploadedBy uploadedAt
      }
    }
  }
`;

const objectiveVersionsByObjectiveQuery = /* GraphQL */ `
  query PMSObjectiveVersionsByObjective($objectiveId: String!, $limit: Int) {
    pmsObjectiveVersionsByObjective(objectiveId: $objectiveId, limit: $limit) {
      items {
        id objectiveId changedFields previousValues newValues
        action changedBy changedByName timestamp statusAtChange
      }
    }
  }
`;

// ---- GraphQL Mutation Strings (PMS-prefixed) ----

const createObjectiveMutation = /* GraphQL */ `
  mutation CreatePMSObjective($input: CreatePMSObjectiveInput!) {
    createPMSObjective(input: $input) { ${OBJ_FIELDS} }
  }
`;

const updateObjectiveMutation = /* GraphQL */ `
  mutation UpdatePMSObjective($input: UpdatePMSObjectiveInput!) {
    updatePMSObjective(input: $input) { ${OBJ_FIELDS} }
  }
`;

const deleteObjectiveMutation = /* GraphQL */ `
  mutation DeletePMSObjective($id: ID!) {
    deletePMSObjective(id: $id) { id }
  }
`;

const createMilestoneMutation = /* GraphQL */ `
  mutation CreatePMSMilestone($input: CreatePMSMilestoneInput!) {
    createPMSMilestone(input: $input) { ${MS_FIELDS} }
  }
`;

const updateMilestoneMutation = /* GraphQL */ `
  mutation UpdatePMSMilestone($input: UpdatePMSMilestoneInput!) {
    updatePMSMilestone(input: $input) { ${MS_FIELDS} }
  }
`;

const deleteMilestoneMutation = /* GraphQL */ `
  mutation DeletePMSMilestone($id: ID!) {
    deletePMSMilestone(id: $id) { id }
  }
`;

const createReviewMutation = /* GraphQL */ `
  mutation CreatePMSReview($input: CreatePMSReviewInput!) {
    createPMSReview(input: $input) { ${REVIEW_FIELDS} }
  }
`;

const updateReviewMutation = /* GraphQL */ `
  mutation UpdatePMSReview($input: UpdatePMSReviewInput!) {
    updatePMSReview(input: $input) { ${REVIEW_FIELDS} }
  }
`;

const createReviewCycleMutation = /* GraphQL */ `
  mutation CreatePMSReviewCycle($input: CreatePMSReviewCycleInput!) {
    createPMSReviewCycle(input: $input) { ${CYCLE_FIELDS} }
  }
`;

const updateReviewCycleMutation = /* GraphQL */ `
  mutation UpdatePMSReviewCycle($input: UpdatePMSReviewCycleInput!) {
    updatePMSReviewCycle(input: $input) { ${CYCLE_FIELDS} }
  }
`;

const deleteReviewCycleMutation = /* GraphQL */ `
  mutation DeletePMSReviewCycle($id: ID!) {
    deletePMSReviewCycle(id: $id) { id }
  }
`;

const createChatMessageMutation = /* GraphQL */ `
  mutation CreatePMSChatMessage($input: CreatePMSChatMessageInput!) {
    createPMSChatMessage(input: $input) {
      id threadType threadId senderId senderName content timestamp
    }
  }
`;

const createAttachmentMutation = /* GraphQL */ `
  mutation CreatePMSAttachment($input: CreatePMSAttachmentInput!) {
    createPMSAttachment(input: $input) {
      id parentType parentId fileName fileType fileSize s3Key uploadedBy uploadedAt
    }
  }
`;

const deleteAttachmentMutation = /* GraphQL */ `
  mutation DeletePMSAttachment($id: ID!) {
    deletePMSAttachment(id: $id) { id }
  }
`;

const createObjectiveVersionMutation = /* GraphQL */ `
  mutation CreatePMSObjectiveVersion($input: CreatePMSObjectiveVersionInput!) {
    createPMSObjectiveVersion(input: $input) {
      id objectiveId changedFields previousValues newValues
      action changedBy changedByName timestamp statusAtChange
    }
  }
`;

const lockObjectivesMutation = /* GraphQL */ `
  mutation LockPMSObjectives($reviewCycleId: String!) {
    lockPMSObjectives(reviewCycleId: $reviewCycleId) { ${CYCLE_FIELDS} }
  }
`;

const unlockObjectivesMutation = /* GraphQL */ `
  mutation UnlockPMSObjectives($reviewCycleId: String!) {
    unlockPMSObjectives(reviewCycleId: $reviewCycleId) { ${CYCLE_FIELDS} }
  }
`;

const openReviewCycleMutation = /* GraphQL */ `
  mutation OpenReviewCycle($reviewCycleId: String!) {
    openReviewCycle(reviewCycleId: $reviewCycleId) { ${CYCLE_FIELDS} }
  }
`;

const closeReviewCycleMutation = /* GraphQL */ `
  mutation CloseReviewCycle($reviewCycleId: String!) {
    closeReviewCycle(reviewCycleId: $reviewCycleId) { ${CYCLE_FIELDS} }
  }
`;


// ---- Mapper Functions ----

function mapObjective(item: any): Objective {
  return {
    id: item.id,
    employeeId: item.employeeId || '',
    employeeName: item.employeeName || '',
    reviewCycleId: item.reviewCycleId || '',
    title: item.title || '',
    description: item.description || '',
    measureOfSuccess: item.measureOfSuccess || '',
    startDate: item.startDate || '',
    endDate: item.endDate || '',
    weightage: item.weightage ?? 0,
    progressPercentage: item.progressPercentage ?? 0,
    finalProgressPercentage: item.finalProgressPercentage ?? undefined,
    managerFinalComment: item.managerFinalComment || '',
    evaluationSummary: item.evaluationSummary as ObjectiveEvaluationSummary | undefined,
    status: item.status as ObjectiveStatus,
    employeeComments: item.employeeComments || '',
    managerComments: item.managerComments || '',
    managerId: item.managerId || '',
    managerName: item.managerName || '',
    isLocked: item.isLocked ?? false,
    trackBy: (item.trackBy as TrackBy | undefined) || undefined,
    createdAt: item.createdAt || new Date().toISOString(),
    updatedAt: item.updatedAt || new Date().toISOString(),
  };
}

function mapMilestone(item: any): Milestone {
  return {
    id: item.id,
    objectiveId: item.objectiveId || '',
    title: item.title || '',
    description: item.description || '',
    dueDate: item.dueDate || '',
    weightage: item.weightage ?? 0,
    progressPercentage: item.progressPercentage ?? 0,
    createdAt: item.createdAt || new Date().toISOString(),
    updatedAt: item.updatedAt || new Date().toISOString(),
  };
}

function parseAwsJson<T>(value: unknown, fallback: T): T {
  if (!value) return fallback;
  if (typeof value === 'string') {
    try { return JSON.parse(value) as T; } catch { return fallback; }
  }
  return value as T;
}

function mapReview(item: any): Review {
  const metadata = parseAwsJson<ReviewMetadata>(item.reviewMetadata, {});
  let ratings = item.objectiveRatings || [];
  if (typeof ratings === 'string') {
    try { ratings = JSON.parse(ratings); } catch { ratings = []; }
  }
  const objectiveRatings = enrichObjectiveRatings((ratings || []).map(mapObjectiveRating));
  return buildReview(item, metadata, objectiveRatings);
}

/**
 * One stored ObjectiveRating, as read back from the objectiveRatings AWSJSON blob.
 *
 * Exported so tests can exercise the real read path instead of a copy. The previous regression
 * tests mirrored this mapping inline, which is exactly why they all passed while it was silently
 * dropping employeeProgressPercentage — a mirror cannot catch a bug in the thing it mirrors.
 */
export function mapObjectiveRating(r: any): ObjectiveRating {
  return {
    objectiveId: r.objectiveId,
    objectiveTitle: r.objectiveTitle || '',
    rating: r.rating as RatingScale,
    /*
     * Carried through explicitly. This mapper lists every field by hand, so a field missing
     * from the list is silently dropped on *every* read — which is what happened when
     * employeeProgressPercentage was added to the model without being added here. The value
     * was written to DynamoDB correctly and then lost the moment it was read back, so
     * employeeProgressOf() fell through to the manager's figure and the manager's own save
     * then persisted that loss.
     *
     * Deliberately no `?? 0`: undefined must stay undefined so employeeProgressOf can tell a
     * record written before the split (fall back to finalProgressPercentage) from one that
     * genuinely holds 0%.
     */
    employeeProgressPercentage: r.employeeProgressPercentage,
    finalProgressPercentage: r.finalProgressPercentage ?? 0,
    comments: r.comments || '',
    /*
     * The manager's own field, and only that.
     *
     * This used to read `r.managerFinalComment || r.comments || ''`, which copied the employee's
     * per-objective self-review note into the manager's field on EVERY read — before any manager
     * had opened the review. From there it reached the "Manager Comment" column, the review PDF
     * and the manager's own comment box, so a freshly submitted self-review appeared to already
     * carry a manager evaluation. The two fields exist precisely to keep the two sides apart, and
     * a read mapper that merges them defeats that at the one point every consumer shares.
     *
     * The legacy fallback that this `||` was there for has not been dropped, only moved to where
     * it can be qualified: managerCommentOf() applies it for records that carry a
     * managerSubmittedAt, i.e. ones a manager really did write. See its docstring.
     */
    managerFinalComment: r.managerFinalComment || '',
    evaluationSummary: r.evaluationSummary as ObjectiveEvaluationSummary | undefined,
    closureStatus: r.closureStatus,
    isReviewed: r.isReviewed,
  };
}

/** The rest of a Review, once its ratings have been mapped. Split out only so the rating
 *  mapper above can be exported and tested on its own. */
function buildReview(item: any, metadata: ReviewMetadata, objectiveRatings: ObjectiveRating[]): Review {
  const workflowStatus = (item.workflowStatus || metadata.workflowStatus) as ReviewWorkflowStatus | undefined;
  const reviewHistory = (item.reviewHistory || metadata.reviewHistory || []) as ReviewHistoryEntry[];
  return {
    id: item.id,
    name: item.name || metadata.name || metadata.reviewName || '',
    reviewName: item.reviewName || metadata.reviewName || metadata.name || '',
    employeeId: item.employeeId || '',
    employeeName: item.employeeName || '',
    reviewCycleId: item.reviewCycleId || '',
    reviewCycleName: item.reviewCycleName || metadata.reviewCycleName || '',
    reviewType: item.reviewType as ReviewType,
    quarter: item.quarter ?? undefined,
    objectiveRatings,
    overallRating: item.overallRating ?? undefined,
    suggestedRating: item.suggestedRating ?? undefined,
    overallComments: item.overallComments || '',
    developmentSuggestions: item.developmentSuggestions || '',
    reviewerId: item.reviewerId || '',
    reviewerName: item.reviewerName || '',
    workflowStatus,
    isAcknowledged: item.isAcknowledged ?? false,
    acknowledgedAt: item.acknowledgedAt || undefined,
    acknowledgedBy: item.acknowledgedBy || undefined,
    reviewHistory,
    reviewMetadata: metadata,
    reopenRequest: metadata.reopenRequest,
    submittedAt: metadata.submittedAt,
    publishedAt: metadata.publishedAt,
    employeeRating: item.employeeRating ?? undefined,
    employeeRatingDescription: item.employeeRatingDescription || undefined,
    employeeComment: item.employeeComment || undefined,
    employeeSubmittedAt: item.employeeSubmittedAt || undefined,
    managerSubmittedAt: item.managerSubmittedAt || undefined,
    timestamp: item.timestamp || '',
    createdAt: item.createdAt || new Date().toISOString(),
    updatedAt: item.updatedAt || new Date().toISOString(),
  };
}

function mapReviewCycle(item: any): ReviewCycle {
  return {
    id: item.id,
    name: item.name || '',
    reviewCycleType: item.reviewCycleType || undefined,
    startDate: item.startDate || '',
    endDate: item.endDate || '',
    midYearDeadline: item.midYearDeadline || '',
    endYearDeadline: item.endYearDeadline || '',
    submissionDeadline: item.submissionDeadline || '',
    status: item.status as ReviewCycleStatus,
    isLocked: item.isLocked ?? false,
    annualCycleId: item.annualCycleId || undefined,
    reviewPhase: (item.reviewPhase as ReviewPhase | undefined) || undefined,
    createdAt: item.createdAt || new Date().toISOString(),
    updatedAt: item.updatedAt || new Date().toISOString(),
  };
}

function mapChatMessage(item: any): ChatMessage {
  return {
    id: item.id,
    threadType: item.threadType as ChatThreadType,
    threadId: item.threadId || '',
    senderId: item.senderId || '',
    senderName: item.senderName || '',
    content: item.content || '',
    timestamp: item.timestamp || '',
  };
}

function mapAttachment(item: any): Attachment {
  return {
    id: item.id,
    parentType: item.parentType as AttachmentParentType,
    parentId: item.parentId || '',
    fileName: item.fileName || '',
    fileType: item.fileType || '',
    fileSize: item.fileSize ?? 0,
    s3Key: item.s3Key || '',
    uploadedBy: item.uploadedBy || '',
    uploadedAt: item.uploadedAt || '',
  };
}

function mapObjectiveVersion(item: any): ObjectiveVersion {
  return {
    id: item.id,
    objectiveId: item.objectiveId || '',
    action: item.action || undefined,
    changedFields: item.changedFields || [],
    previousValues: typeof item.previousValues === 'string'
      ? JSON.parse(item.previousValues)
      : (item.previousValues || {}),
    newValues: typeof item.newValues === 'string'
      ? JSON.parse(item.newValues)
      : (item.newValues || {}),
    changedBy: item.changedBy || '',
    changedByName: item.changedByName || '',
    timestamp: item.timestamp || '',
    statusAtChange: item.statusAtChange as ObjectiveStatus,
  };
}

// ---- Service Result Types ----

export interface PMSServiceResult<T> {
  success: boolean;
  entity?: T;
  errors: string[];
}

// ---- GraphQL PMS Service ----

export class GraphQLPMSService {

  // ---- Objective CRUD ----

  static async createObjective(
    input: CreateObjectiveInput,
    employeeId: string,
    employeeName: string,
    managerId: string,
    managerName: string,
    asDraft: boolean = false
  ): Promise<PMSServiceResult<Objective>> {
    try {
      const gqlInput: any = {
        reviewCycleId: input.reviewCycleId,
        employeeId,
        employeeName,
        title: input.title,
        description: input.description,
        measureOfSuccess: input.measureOfSuccess,
        startDate: input.startDate,
        endDate: input.endDate,
        weightage: Number(input.weightage) || 0,
        employeeComments: input.employeeComments || '',
        managerId: managerId || 'unassigned',
        managerName: managerName || 'Unassigned',
        trackBy: input.trackBy || TrackBy.PERCENT,
      };

      /*
       * progressPercentage is deliberately NOT in the input above.
       *
       * CreatePMSObjectiveInput does not declare it — the create resolver hard-codes
       * "progressPercentage": 0 — and AppSync rejects the WHOLE mutation when an input object
       * carries a field its type does not declare. It does not drop the extra field and keep
       * the rest, exactly as naming an undeployed field in a selection set empties a read.
       *
       * This was sent "only when present", which reads as a safe conditional and never was:
       * the form initialises its slider to 0, so input.progressPercentage is always a number
       * and the guard never fired once. Every objective create failed validation, both callers
       * ignored the failure, and the employee was returned to a list without their goal.
       *
       * A starting progress is applied after the create instead, through the update mutation,
       * whose input type does declare the field. See pms-create-objective-contract.test.ts.
       */
      const startingProgress =
        input.progressPercentage === undefined
          ? 0
          : Math.round(Math.min(100, Math.max(0, Number(input.progressPercentage) || 0)));

      console.log('[GraphQL PMS] createObjective input:', JSON.stringify(gqlInput, null, 2));

      const result = await graphqlWithLog({
        query: createObjectiveMutation,
        variables: { input: gqlInput },
        ...getAuthMode(),
      } as any);

      const data = getData(result);
      let objective = mapObjective(data?.createPMSObjective);

      /*
       * The resolver always writes 0, so a goal created at, say, 40% needs one follow-up
       * write. An update rather than a re-sent create: it is keyed on the row that now
       * exists, so it cannot leave a second objective behind the way a retried create could.
       *
       * A failure here loses the starting figure, not the goal, so it does not fail the save.
       */
      if (objective?.id && startingProgress > 0) {
        const withProgress = await GraphQLPMSService.updateObjective(objective.id, {
          progressPercentage: startingProgress,
        });
        if (withProgress.success && withProgress.entity) objective = withProgress.entity;
      }

      if (objective?.id) {
        await GraphQLPMSService.createObjectiveHistory(objective, {
          action: 'Objective Created',
          changedFields: ['objective'],
          previousValues: {},
          newValues: {
            title: objective.title,
            status: objective.status,
            progressPercentage: objective.progressPercentage,
          },
          changedBy: employeeId,
          changedByName: employeeName,
        });
      }
      clearPmsReadCaches();
      return { success: true, entity: objective, errors: [] };
    } catch (error: any) {
      console.error('[GraphQL PMS] createObjective error:', JSON.stringify(error, null, 2));
      console.error('[GraphQL PMS] createObjective error details:', error?.errors?.[0]);
      return { success: false, errors: [error?.errors?.[0]?.message || error.message || 'Failed to create objective'] };
    }
  }

  static async updateObjective(
    objectiveId: string,
    updates: UpdateObjectiveInput,
    audit?: { action?: string; changedBy?: string; changedByName?: string; previousObjective?: Objective }
  ): Promise<PMSServiceResult<Objective>> {
    try {
      const gqlInput: any = { id: objectiveId, ...updates };

      const result = await graphqlWithLog({
        query: updateObjectiveMutation,
        variables: { input: gqlInput },
        ...getAuthMode(),
      } as any);

      const data = getData(result);
      const objective = mapObjective(data?.updatePMSObjective);
      if (objective?.id && audit?.changedBy) {
        const changedFields = Object.keys(updates);
        const previousValues = audit.previousObjective
          ? changedFields.reduce<Record<string, unknown>>((acc, field) => {
              acc[field] = (audit.previousObjective as any)[field];
              return acc;
            }, {})
          : {};
        await GraphQLPMSService.createObjectiveHistory(objective, {
          action: audit.action || GraphQLPMSService.inferObjectiveAction(updates),
          changedFields,
          previousValues,
          newValues: updates as Record<string, unknown>,
          changedBy: audit.changedBy,
          changedByName: audit.changedByName || audit.changedBy,
        });
      }
      clearPmsReadCaches();
      return { success: true, entity: objective, errors: [] };
    } catch (error: any) {
      console.error('[GraphQL PMS] updateObjective error:', error);
      return { success: false, errors: [error?.errors?.[0]?.message || error.message || 'Failed to update objective'] };
    }
  }

  static async deleteObjective(objectiveId: string): Promise<{ success: boolean; errors: string[] }> {
    try {
      await graphqlWithLog({
        query: deleteObjectiveMutation,
        variables: { id: objectiveId },
        ...getAuthMode(),
      } as any);
      clearPmsReadCaches();
      return { success: true, errors: [] };
    } catch (error: any) {
      console.error('[GraphQL PMS] deleteObjective error:', error);
      return { success: false, errors: [error?.errors?.[0]?.message || error.message || 'Failed to delete objective'] };
    }
  }

  static async getObjective(objectiveId: string): Promise<Objective | undefined> {
    try {
      const result = await graphqlWithLog({
        query: getObjectiveQuery,
        variables: { id: objectiveId },
        ...getAuthMode(),
      } as any);
      const data = getData(result);
      if (!data?.getPMSObjective) return undefined;
      console.log('[GraphQL PMS][ObjectiveRaw][getObjective]', {
        objectiveId,
        rawProgressPercentage: data.getPMSObjective.progressPercentage,
        rawObjective: data.getPMSObjective,
      });
      return mapObjective(data.getPMSObjective);
    } catch (error) {
      console.error('[GraphQL PMS] getObjective error:', error);
      return undefined;
    }
  }

  static async getEmployeeObjectives(employeeId: string, reviewCycleId?: string): Promise<Objective[]> {
    try {
      const startedAt = performance.now();
      // Queried across both email domains: objectives set before the domain move are
      // filed under the employee's old address, and asking only for the address the
      // session reports would return a clean empty list.
      const rawItems = await collectAcrossIdentities(employeeId, async candidate => {
        const result = await graphqlWithLog({
          query: objectivesByEmployeeQuery,
          variables: { employeeId: candidate, limit: 100 },
          ...getAuthMode(),
        } as any);
        return getData(result)?.pmsObjectivesByEmployee?.items || [];
      });
      const fetchMs = Math.round(performance.now() - startedAt);
      console.log('[GraphQL PMS][ObjectiveRaw][getEmployeeObjectives]', {
        employeeId,
        reviewCycleId,
        objectives: rawItems
          .filter((item: any) => item && item.id)
          .map((item: any) => ({
            id: item.id,
            progressPercentage: item.progressPercentage,
            currentProgress: item.currentProgress,
            progress: item.progress,
          })),
      });
      const mappingStartedAt = performance.now();
      let items = rawItems
        .filter((item: any) => item && item.id)
        .map((item: any) => {
          try { return mapObjective(item); } catch { return null; }
        })
        .filter(Boolean) as Objective[];
      const mappingMs = Math.round(performance.now() - mappingStartedAt);
      const filteringStartedAt = performance.now();
      if (reviewCycleId) {
        items = items.filter((o: Objective) => o.reviewCycleId === reviewCycleId);
      }
      console.log('[GraphQL PMS][Profile] getEmployeeObjectives', {
        fetchDataMs: fetchMs,
        mappingMs,
        filteringMs: Math.round(performance.now() - filteringStartedAt),
        employeeId,
        reviewCycleId,
        rawCount: rawItems.length,
        returnedCount: items.length,
      });
      logPmsRead({
        operation: 'pmsObjectivesByEmployee',
        table: PMS_TABLES.objectives,
        reviewCycleId,
        employeeId,
        recordCount: items.length,
      });
      return items;
    } catch (error: any) {
      // Recover partial data from AppSync partial errors
      const partial = error?.data?.pmsObjectivesByEmployee?.items;
      if (partial && Array.isArray(partial)) {
        console.warn('[GraphQL PMS] getEmployeeObjectives partial recovery:', partial.length, 'items');
        let items = partial
          .filter((item: any) => item && item.id)
          .map((item: any) => {
            try { return mapObjective(item); } catch { return null; }
          })
          .filter(Boolean) as Objective[];
        if (reviewCycleId) {
          items = items.filter((o: Objective) => o.reviewCycleId === reviewCycleId);
        }
        return items;
      }
      console.error('[GraphQL PMS] getEmployeeObjectives error:', error);
      return [];
    }
  }

  static async getObjectivesByManager(managerId: string, reviewCycleId?: string): Promise<Objective[]> {
    try {
      const cacheKey = `objectivesByManager|${managerId}|${reviewCycleId || ''}`;
      const cached = getCachedObjectives(cacheKey);
      if (cached) {
        console.log('[GraphQL PMS][Profile] getObjectivesByManager cache hit', {
          managerId,
          reviewCycleId,
          returnedCount: cached.length,
        });
        return cached;
      }
      const startedAt = performance.now();
      // Both domains: a manager signing in on the new domain would otherwise lose every
      // objective filed against their old-domain address.
      const rawItems = await collectAcrossIdentities(managerId, async candidate => {
        const result = await graphqlWithLog({
          query: objectivesByManagerQuery,
          variables: { managerId: candidate, limit: 100 },
          ...getAuthMode(),
        } as any);
        return getData(result)?.pmsObjectivesByManager?.items || [];
      });
      const mappingStartedAt = performance.now();
      let items = rawItems.map(mapObjective);
      const mappingMs = Math.round(performance.now() - mappingStartedAt);
      const filteringStartedAt = performance.now();
     if (reviewCycleId) {
  console.log(
    '[PMS] Selected reviewCycleId:',
    reviewCycleId
  );

  console.log(
    '[PMS] Objective cycle IDs:',
    items.map((o: Objective) => ({
      id: o.id,
      title: o.title,
      reviewCycleId: o.reviewCycleId
    }))
  );

  items = items.filter((o: Objective) =>
    Boolean(o.reviewCycleId) &&
    String(o.reviewCycleId).trim() === String(reviewCycleId).trim()
  );
}
      console.log('[GraphQL PMS][Profile] getObjectivesByManager', {
        fetchDataMs: Math.round(mappingStartedAt - startedAt),
        mappingMs,
        filteringMs: Math.round(performance.now() - filteringStartedAt),
        managerId,
        reviewCycleId,
        rawCount: rawItems.length,
        returnedCount: items.length,
      });
      setCachedObjectives(cacheKey, items);
      return items;
    } catch (error) {
      console.error('[GraphQL PMS] getObjectivesByManager error:', error);
      return [];
    }
  }

  static async getPendingApprovalObjectivesByManager(managerId: string): Promise<Objective[]> {
    try {
      const cacheKey = `pendingApprovalObjectivesByManager|${managerId}`;
      const cached = getCachedObjectives(cacheKey);
      if (cached) return cached;
      const rawItems = await collectAcrossIdentities(managerId, async candidate => {
        const result = await graphqlWithLog({
          query: pendingApprovalObjectivesByManagerQuery,
          variables: { managerId: candidate, limit: 100 },
          ...getAuthMode(),
        } as any);
        return getData(result)?.pmsObjectivesByManager?.items || [];
      });
      const objectives = rawItems
        .filter((item: any) => item && item.id)
        .map(mapObjective)
        .filter((objective: Objective) => objective.status === ObjectiveStatus.SUBMITTED);
      setCachedObjectives(cacheKey, objectives);
      return objectives;
    } catch (error) {
      console.error('[GraphQL PMS] getPendingApprovalObjectivesByManager error:', error);
      return [];
    }
  }

  static async getObjectivesByReviewCycle(reviewCycleId: string): Promise<Objective[]> {
    try {
      const cacheKey = `objectivesByReviewCycle|${reviewCycleId}`;
      const cached = getCachedObjectives(cacheKey);
      if (cached) return cached;
      const result = await graphqlWithLog({
        query: objectivesByReviewCycleQuery,
        variables: { reviewCycleId, limit: 100 },
        ...getAuthMode(),
      } as any);
      const data = getData(result);
      const objectives = (data?.pmsObjectivesByReviewCycle?.items || []).map(mapObjective);
      setCachedObjectives(cacheKey, objectives);
      return objectives;
    } catch (error) {
      console.error('[GraphQL PMS] getObjectivesByReviewCycle error:', error);
      return [];
    }
  }

  static async getPendingApprovalObjectivesByStatus(status: ObjectiveStatus): Promise<Objective[]> {
    try {
      const cacheKey = `pendingApprovalObjectivesByStatus|${status}`;
      const cached = getCachedObjectives(cacheKey);
      if (cached) return cached;
      const result = await graphqlWithLog({
        query: pendingApprovalObjectivesByStatusQuery,
        variables: { status, limit: 100 },
        ...getAuthMode(),
      } as any);
      const data = getData(result);
      const objectives = (data?.pmsObjectivesByStatus?.items || [])
        .filter((item: any) => item && item.id)
        .map(mapObjective);
      setCachedObjectives(cacheKey, objectives);
      return objectives;
    } catch (error) {
      console.error('[GraphQL PMS] getPendingApprovalObjectivesByStatus error:', error);
      return [];
    }
  }

  static async getObjectivesByStatus(status: ObjectiveStatus): Promise<Objective[]> {
    try {
      const cacheKey = `objectivesByStatus|${status}`;
      const cached = getCachedObjectives(cacheKey);
      if (cached) return cached;
      const result = await graphqlWithLog({
        query: objectivesByStatusQuery,
        variables: { status, limit: 100 },
        ...getAuthMode(),
      } as any);
      const data = getData(result);
      const objectives = (data?.pmsObjectivesByStatus?.items || []).map(mapObjective);
      setCachedObjectives(cacheKey, objectives);
      return objectives;
    } catch (error) {
      console.error('[GraphQL PMS] getObjectivesByStatus error:', error);
      return [];
    }
  }

  static async getAllObjectives(): Promise<Objective[]> {
    try {
      const cacheKey = 'allObjectives';
      const cached = getCachedObjectives(cacheKey);
      if (cached) return cached;
      const startedAt = performance.now();
      const result = await graphqlWithLog({
        query: listObjectivesQuery,
        variables: { limit: 500 },
        ...getAuthMode(),
      } as any);
      const data = getData(result);
      const rawItems = data?.listPMSObjectives?.items || [];
      const mappingStartedAt = performance.now();
      const objectives = rawItems.map(mapObjective);
      console.log('[GraphQL PMS][Profile] getAllObjectives', {
        fetchDataMs: Math.round(mappingStartedAt - startedAt),
        mappingMs: Math.round(performance.now() - mappingStartedAt),
        rawCount: rawItems.length,
        returnedCount: objectives.length,
      });
      setCachedObjectives(cacheKey, objectives);
      return objectives;
    } catch (error) {
      console.error('[GraphQL PMS] getAllObjectives error:', error);
      return [];
    }
  }


  // ---- Milestone CRUD ----

  static async createMilestone(input: CreateMilestoneInput): Promise<PMSServiceResult<Milestone>> {
    try {
      const gqlInput: any = {
        objectiveId: input.objectiveId,
        title: input.title,
        description: input.description,
        dueDate: input.dueDate,
        weightage: input.weightage ?? 0,
        progressPercentage: input.progressPercentage ?? 0,
      };

      const result = await graphqlWithLog({
        query: createMilestoneMutation,
        variables: { input: gqlInput },
        ...getAuthMode(),
      } as any);

      const data = getData(result);
      const milestone = mapMilestone(data?.createPMSMilestone);
      return { success: true, entity: milestone, errors: [] };
    } catch (error: any) {
      console.error('[GraphQL PMS] createMilestone error:', error);
      return { success: false, errors: [error?.errors?.[0]?.message || error.message || 'Failed to create milestone'] };
    }
  }

  static async updateMilestone(
    milestoneId: string,
    updates: Partial<CreateMilestoneInput>
  ): Promise<PMSServiceResult<Milestone>> {
    try {
      const gqlInput: any = { id: milestoneId, ...updates };
      delete gqlInput.objectiveId; // objectiveId is not updatable

      const result = await graphqlWithLog({
        query: updateMilestoneMutation,
        variables: { input: gqlInput },
        ...getAuthMode(),
      } as any);

      const data = getData(result);
      const milestone = mapMilestone(data?.updatePMSMilestone);
      return { success: true, entity: milestone, errors: [] };
    } catch (error: any) {
      console.error('[GraphQL PMS] updateMilestone error:', error);
      return { success: false, errors: [error?.errors?.[0]?.message || error.message || 'Failed to update milestone'] };
    }
  }

  static async deleteMilestone(milestoneId: string): Promise<{ success: boolean; errors: string[] }> {
    try {
      await graphqlWithLog({
        query: deleteMilestoneMutation,
        variables: { id: milestoneId },
        ...getAuthMode(),
      } as any);
      return { success: true, errors: [] };
    } catch (error: any) {
      console.error('[GraphQL PMS] deleteMilestone error:', error);
      return { success: false, errors: [error?.errors?.[0]?.message || error.message || 'Failed to delete milestone'] };
    }
  }

  static async getMilestones(objectiveId: string): Promise<Milestone[]> {
    try {
      const startedAt = performance.now();
      const result = await graphqlWithLog({
        query: listMilestonesQuery,
        variables: { objectiveId, limit: 100 },
        ...getAuthMode(),
      } as any);
      const data = getData(result);
      const rawItems = data?.pmsMilestonesByObjective?.items || [];
      const mappingStartedAt = performance.now();
      const milestones = rawItems.map(mapMilestone);
      console.log('[GraphQL PMS][Profile] getMilestones', {
        fetchDataMs: Math.round(mappingStartedAt - startedAt),
        mappingMs: Math.round(performance.now() - mappingStartedAt),
        objectiveId,
        rawCount: rawItems.length,
        returnedCount: milestones.length,
        repeatedLookupRisk: true,
      });
      return milestones;
    } catch (error) {
      console.error('[GraphQL PMS] getMilestones error:', error);
      return [];
    }
  }

  // ---- Review Operations ----

  static async createReview(
    input: CreateReviewInput,
    reviewerId: string,
    reviewerName: string
  ): Promise<PMSServiceResult<Review>> {
    try {
      const reviewMetadata = {
        ...(input.reviewMetadata || {}),
        ...((input.name || input.reviewName) ? {
          name: input.name || input.reviewName,
          reviewName: input.reviewName || input.name,
        } : {}),
        ...(input.reviewCycleName ? { reviewCycleName: input.reviewCycleName } : {}),
      };
      const gqlInput: any = {
        employeeId: input.employeeId,
        employeeName: '', // will be filled by caller
        reviewCycleId: input.reviewCycleId,
        reviewType: input.reviewType,
        quarter: input.quarter,
        objectiveRatings: JSON.stringify(enrichObjectiveRatings(input.objectiveRatings)),
        overallRating: input.overallRating,
        overallComments: input.overallComments,
        developmentSuggestions: input.developmentSuggestions,
        reviewerId,
        reviewerName,
        workflowStatus: input.workflowStatus || ReviewWorkflowStatus.DRAFT,
        isAcknowledged: false,
        reviewMetadata: Object.keys(reviewMetadata).length > 0 ? JSON.stringify(reviewMetadata) : undefined,
      };

      const result = await graphqlWithLog({
        query: createReviewMutation,
        variables: { input: gqlInput },
        ...getAuthMode(),
      } as any);

      const data = getData(result);
      const review = mapReview(data?.createPMSReview);
      clearPmsReadCaches();
      return { success: true, entity: review, errors: [] };
    } catch (error: any) {
      console.error('[GraphQL PMS] createReview error:', error);
      return { success: false, errors: [error?.errors?.[0]?.message || error.message || 'Failed to create review'] };
    }
  }

  static async updateReview(
    reviewId: string,
    input: UpdateReviewInput
  ): Promise<PMSServiceResult<Review>> {
    try {
      const gqlInput: any = { id: reviewId };
      if (input.objectiveRatings) {
        gqlInput.objectiveRatings = JSON.stringify(enrichObjectiveRatings(input.objectiveRatings));
      }
      if (input.overallRating !== undefined) gqlInput.overallRating = input.overallRating;
      if (input.overallComments !== undefined) gqlInput.overallComments = input.overallComments;
      if (input.developmentSuggestions !== undefined) {
        gqlInput.developmentSuggestions = input.developmentSuggestions;
      }
      if (input.workflowStatus) gqlInput.workflowStatus = input.workflowStatus;
      if (input.isAcknowledged !== undefined) gqlInput.isAcknowledged = input.isAcknowledged;
      if (input.acknowledgedAt) gqlInput.acknowledgedAt = input.acknowledgedAt;
      if (input.acknowledgedBy) gqlInput.acknowledgedBy = input.acknowledgedBy;
      if (input.reviewMetadata) gqlInput.reviewMetadata = JSON.stringify(input.reviewMetadata);
      if (input.employeeRating !== undefined) gqlInput.employeeRating = input.employeeRating;
      if (input.employeeRatingDescription !== undefined) gqlInput.employeeRatingDescription = input.employeeRatingDescription;
      if (input.employeeComment !== undefined) gqlInput.employeeComment = input.employeeComment;
      if (input.employeeSubmittedAt) gqlInput.employeeSubmittedAt = input.employeeSubmittedAt;
      if (input.managerSubmittedAt) gqlInput.managerSubmittedAt = input.managerSubmittedAt;
      if (input.reviewerId) gqlInput.reviewerId = input.reviewerId;
      if (input.reviewerName) gqlInput.reviewerName = input.reviewerName;

      const result = await graphqlWithLog({
        query: updateReviewMutation,
        variables: { input: gqlInput },
        ...getAuthMode(),
      } as any);

      const data = getData(result);
      const review = mapReview(data?.updatePMSReview);
      clearPmsReadCaches();
      return { success: true, entity: review, errors: [] };
    } catch (error: any) {
      console.error('[GraphQL PMS] updateReview error:', error);
      return { success: false, errors: [error?.errors?.[0]?.message || error.message || 'Failed to update review'] };
    }
  }

  /**
   * Employee's own self-review submission. Finds the existing review row for this
   * employee+cycle+reviewType if one already exists (e.g. a manager started one, or
   * the employee is re-entering after a partial save) and updates it in place;
   * otherwise seeds a brand new row with no reviewer identity yet — the manager's
   * later submission fills that in via updateReview. This keeps employee and manager
   * input on the SAME record rather than two divergent rows.
   */
  static async submitSelfReview(
    input: SubmitSelfReviewInput
  ): Promise<PMSServiceResult<Review>> {
    try {
      /*
       * The employee's existing review for this cycle — keyed on employee + reviewCycleId ONLY.
       *
       * This used to be `.find(r => r.reviewType === input.reviewType)`, and reviewType is
       * precisely the field the two creation paths disagreed about: the manager's handleCreate
       * wrote END_YEAR (or QUARTERLY for a HIGH_LEVEL cycle) while this path computed MID_YEAR
       * for anything that was not explicitly YEAR_END. So on a cycle with no reviewCycleType —
       * the common case — this lookup could not see the manager's record, fell through to the
       * create branch below, and wrote a SECOND review for the same employee and cycle. The pair
       * then derived opposite statuses (Waiting Employee / Waiting Manager) because each carried
       * different submission timestamps.
       *
       * PMS expects one review per employee per review cycle, so that pair is the key to match
       * on. getEmployeeReviews already filters to the cycle client-side, and
       * resolveCanonicalReview picks the furthest-progressed record if duplicates are already in
       * the data — so an existing submission is updated rather than shadowed by a new row.
       */
      const cycleReviews = await GraphQLPMSService.getEmployeeReviews(input.employeeId, input.reviewCycleId);
      const existing = resolveCanonicalReview(cycleReviews, input.employeeId, input.reviewCycleId);
      if (cycleReviews.length > 1) {
        console.warn('[GraphQL PMS] submitSelfReview found duplicate reviews for one employee+cycle', {
          employeeId: input.employeeId,
          reviewCycleId: input.reviewCycleId,
          reviewIds: cycleReviews.map(r => r.id),
          reviewTypes: cycleReviews.map(r => r.reviewType),
          updating: existing?.id,
        });
      }

      const employeeSubmittedAt = new Date().toISOString();

      if (existing) {
        return GraphQLPMSService.updateReview(existing.id, {
          objectiveRatings: input.objectiveRatings,
          employeeRating: input.employeeRating,
          employeeRatingDescription: input.employeeRatingDescription,
          employeeComment: input.employeeComment,
          employeeSubmittedAt,
        });
      }

      const gqlInput: any = {
        employeeId: input.employeeId,
        employeeName: input.employeeName,
        reviewCycleId: input.reviewCycleId,
        reviewType: input.reviewType,
        objectiveRatings: JSON.stringify(enrichObjectiveRatings(input.objectiveRatings)),
        employeeRating: input.employeeRating,
        employeeRatingDescription: input.employeeRatingDescription,
        employeeComment: input.employeeComment,
        employeeSubmittedAt,
        workflowStatus: ReviewWorkflowStatus.IN_PROGRESS,
        /*
         * The reviewer is stamped at creation, from the employee's own objectives.
         *
         * These were simply never sent, so every lazily-created review was stored with no
         * reviewer. Team Reviews still listed the employee because it finds them through the
         * manager's reporting line rather than through this record — but My Reviews > Overview
         * reads `selectedReview.reviewerName` directly and had nothing to show, which is the
         * "Manager: Not assigned" contradiction.
         *
         * Omitted rather than written blank when unknown: an empty string would look like a
         * deliberately cleared reviewer to anything reading it later.
         */
        ...(input.reviewerId ? { reviewerId: input.reviewerId } : {}),
        ...(input.reviewerName ? { reviewerName: input.reviewerName } : {}),
      };

      const result = await graphqlWithLog({
        query: createReviewMutation,
        variables: { input: gqlInput },
        ...getAuthMode(),
      } as any);

      const data = getData(result);
      const review = mapReview(data?.createPMSReview);
      clearPmsReadCaches();
      return { success: true, entity: review, errors: [] };
    } catch (error: any) {
      console.error('[GraphQL PMS] submitSelfReview error:', error);
      return { success: false, errors: [error?.errors?.[0]?.message || error.message || 'Failed to submit self review'] };
    }
  }

  /**
   * Manager sends the self-review back to the employee for revision.
   *
   * Writes only reviewMetadata: returnedAt is what every permission helper compares against
   * employeeSubmittedAt to decide whose turn it is. Deliberately does NOT clear
   * employeeSubmittedAt — updateReview guards that field with a truthy check and cannot write a
   * null, and the original submission time is audit history the timeline reads.
   *
   * Deliberately does NOT touch objectiveRatings, so the manager's partial evaluation and the
   * employee's submitted figures both survive the round trip, and it does NOT touch the
   * objectives, which stay locked exactly as HR left them.
   */
  static async returnSelfReviewToEmployee(
    review: Review,
    reason: string,
    actor: { id: string; name: string }
  ): Promise<PMSServiceResult<Review>> {
    const trimmed = (reason || '').trim();
    if (!trimmed) {
      return { success: false, errors: ['A reason is required to return a review.'] };
    }

    const returnedAt = new Date().toISOString();
    const history: ReviewHistoryEntry[] = [
      ...(review.reviewHistory || []),
      {
        id: `${review.id}-returned-${returnedAt}`,
        action: ReviewHistoryAction.REVIEW_RETURNED,
        performedBy: actor.id,
        performedByName: actor.name,
        timestamp: returnedAt,
        // newValue carries the reason: the entry shape has no dedicated notes field, and this
        // is where the reopen flow already puts its explanatory text.
        newValue: trimmed,
      },
    ];

    return GraphQLPMSService.updateReview(review.id, {
      reviewMetadata: {
        ...(review.reviewMetadata || {}),
        returnedAt,
        returnReason: trimmed,
        returnedBy: actor.id,
        returnedByName: actor.name,
        reviewHistory: history,
      },
    } as any);
  }

  static async getReview(reviewId: string): Promise<Review | undefined> {
    try {
      const result = await graphqlWithLog({
        query: getReviewQuery,
        variables: { id: reviewId },
        ...getAuthMode(),
      } as any);
      const data = getData(result);
      const review = data?.getPMSReview ? mapReview(data.getPMSReview) : undefined;
      logPmsRead({
        operation: 'getPMSReview',
        table: PMS_TABLES.reviews,
        reviewId,
        reviewCycleId: review?.reviewCycleId,
        employeeId: review?.employeeId,
        recordCount: review ? 1 : 0,
      });
      return review;
    } catch (error) {
      console.error('[GraphQL PMS] getReview error:', error);
      return undefined;
    }
  }

  static async getEmployeeReviews(employeeId: string, reviewCycleId?: string): Promise<Review[]> {
    try {
      const cacheKey = `employeeReviews|${employeeId}|${reviewCycleId || ''}`;
      const cached = getCachedReviews(cacheKey);
      if (cached) {
        logPmsRead({
          operation: 'pmsReviewsByEmployee (cache)',
          table: PMS_TABLES.reviews,
          reviewCycleId,
          employeeId,
          recordCount: cached.length,
        });
        return cached;
      }

      console.info('[GraphQL PMS][ReviewRequestTrace] pmsReviewsByEmployee request', {
        employeeId,
        requestedReviewCycleId: reviewCycleId || '',
        variables: { employeeId, limit: 100 },
        serverSideCycleFilter: false,
      });
      // Both domains — a review written against the old-domain address is still this
      // employee's review.
      const rawItems = await collectAcrossIdentities(employeeId, async candidate => {
        const result = await graphqlWithLog({
          query: reviewsByEmployeeQuery,
          variables: { employeeId: candidate, limit: 100 },
          ...getAuthMode(),
        } as any);
        return getData(result)?.pmsReviewsByEmployee?.items || [];
      });
      let items = rawItems.map(mapReview);
      console.groupCollapsed('[GraphQL PMS][ReviewRequestTrace] pmsReviewsByEmployee raw result');
      console.info({
        employeeId,
        requestedReviewCycleId: reviewCycleId || '',
        rawReviewCount: rawItems.length,
        clientSideCycleFilter: Boolean(reviewCycleId),
      });
      console.table(items.map((review: Review) => ({
        reviewId: review.id,
        'review.cycleId': review.reviewCycleId,
        reviewCycleId: review.reviewCycleId,
        managerId: review.reviewerId,
        employeeId: review.employeeId,
        status: review.workflowStatus || review.reviewMetadata?.workflowStatus || '',
        isAcknowledged: Boolean(review.isAcknowledged),
        reviewType: review.reviewType,
      })));
      console.groupEnd();
      if (reviewCycleId) {
        items = items.filter((r: Review) => r.reviewCycleId === reviewCycleId);
      }
      console.info('[GraphQL PMS][ReviewRequestTrace] pmsReviewsByEmployee after client filter', {
        employeeId,
        requestedReviewCycleId: reviewCycleId || '',
        returnedReviewCount: items.length,
        discardedByCycleFilter: rawItems.length - items.length,
      });
      logPmsRead({
        operation: 'pmsReviewsByEmployee',
        table: PMS_TABLES.reviews,
        reviewCycleId,
        employeeId,
        recordCount: items.length,
      });
      setCachedReviews(cacheKey, items);
      return items;
    } catch (error) {
      console.error('[GraphQL PMS] getEmployeeReviews error:', error);
      return [];
    }
  }

  static async getReviewsByReviewCycle(reviewCycleId: string): Promise<Review[]> {
    try {
      const cacheKey = `reviewsByCycle|${reviewCycleId}`;
      const cached = getCachedReviews(cacheKey);
      if (cached) {
        logPmsRead({
          operation: 'pmsReviewsByReviewCycle (cache)',
          table: PMS_TABLES.reviews,
          reviewCycleId,
          recordCount: cached.length,
        });
        return cached;
      }

      const rawItems: any[] = [];
      let nextToken: string | null | undefined;
      let page = 0;
      do {
        page += 1;
        console.info('[GraphQL PMS][ReviewRequestTrace] pmsReviewsByReviewCycle request', {
          reviewCycleId,
          variables: { reviewCycleId, limit: 100, nextToken },
          page,
          serverSideCycleFilter: true,
        });
        const result = await graphqlWithLog({
          query: reviewsByReviewCycleQuery,
          variables: { reviewCycleId, limit: 100, nextToken },
          ...getAuthMode(),
        } as any);
        const data = getData(result);
        const connection = data?.pmsReviewsByReviewCycle;
        rawItems.push(...(connection?.items || []));
        nextToken = connection?.nextToken;
      } while (nextToken);
      const reviews = rawItems.map(mapReview);
      logPmsRead({
        operation: 'pmsReviewsByReviewCycle',
        table: PMS_TABLES.reviews,
        reviewCycleId,
        recordCount: reviews.length,
      });
      console.info('[GraphQL PMS][ReviewsByCycle] Pagination complete', {
        reviewCycleId,
        pagesLoaded: page,
        totalReviewRecordsFound: reviews.length,
        reviewIds: reviews.map((review) => review.id),
      });
      console.table(reviews.map((review) => ({
        reviewId: review.id,
        'review.cycleId': review.reviewCycleId,
        reviewCycleId: review.reviewCycleId,
        managerId: review.reviewerId,
        employeeId: review.employeeId,
        status: review.workflowStatus || review.reviewMetadata?.workflowStatus || '',
        isAcknowledged: Boolean(review.isAcknowledged),
        reviewType: review.reviewType,
      })));
      setCachedReviews(cacheKey, reviews);
      return reviews;
    } catch (error) {
      console.error('[GraphQL PMS] getReviewsByReviewCycle error:', error);
      return [];
    }
  }

  // ---- Review Cycle Operations ----

  static async createReviewCycle(input: CreateReviewCycleInput): Promise<PMSServiceResult<ReviewCycle>> {
    try {
      const result = await graphqlWithLog({
        query: createReviewCycleMutation,
        variables: { input },
        ...getAuthMode(),
      } as any);
      const data = getData(result);
      let cycle = mapReviewCycle(data?.createPMSReviewCycle);
      
      // Workaround: if annualCycleId was provided but not stored by the resolver,
      // immediately update the cycle to set it. This handles the case where the
      // old resolver (without annualCycleId support) is still deployed.
      if (input.annualCycleId && !cycle.annualCycleId) {
        console.log('[GraphQL PMS] createReviewCycle: annualCycleId not stored by resolver, updating...');
        const updateResult = await GraphQLPMSService.updateReviewCycle(cycle.id, {
          annualCycleId: input.annualCycleId,
        });
        if (updateResult.success && updateResult.entity) {
          cycle = updateResult.entity;
        }
      }
      
      clearPmsReadCaches();
      return { success: true, entity: cycle, errors: [] };
    } catch (error: any) {
      console.error('[GraphQL PMS] createReviewCycle error:', error);
      return { success: false, errors: [error?.errors?.[0]?.message || error.message || 'Failed to create review cycle'] };
    }
  }

  static async updateReviewCycle(
    cycleId: string,
    updates: Partial<CreateReviewCycleInput> & { status?: ReviewCycleStatus; isLocked?: boolean }
  ): Promise<PMSServiceResult<ReviewCycle>> {
    try {
      const gqlInput: any = { id: cycleId, ...updates };

      const result = await graphqlWithLog({
        query: updateReviewCycleMutation,
        variables: { input: gqlInput },
        ...getAuthMode(),
      } as any);

      const data = getData(result);
      const cycle = mapReviewCycle(data?.updatePMSReviewCycle);
      clearPmsReadCaches();
      return { success: true, entity: cycle, errors: [] };
    } catch (error: any) {
      console.error('[GraphQL PMS] updateReviewCycle error:', error);
      return { success: false, errors: [error?.errors?.[0]?.message || error.message || 'Failed to update review cycle'] };
    }
  }

  static async getReviewCycle(cycleId: string): Promise<ReviewCycle | undefined> {
    try {
      const result = await graphqlWithLog({
        query: getReviewCycleQuery,
        variables: { id: cycleId },
        ...getAuthMode(),
      } as any);
      const data = getData(result);
      if (!data?.getPMSReviewCycle) return undefined;
      return mapReviewCycle(data.getPMSReviewCycle);
    } catch (error) {
      console.error('[GraphQL PMS] getReviewCycle error:', error);
      return undefined;
    }
  }

  static async getAllReviewCycles(): Promise<ReviewCycle[]> {
    try {
      const cacheKey = 'allReviewCycles';
      const cached = getCachedCycles(cacheKey);
      if (cached) {
        console.info('[GraphQL PMS][ReviewCycles] Returning cached cycles', {
          totalCycles: cached.length,
          source: 'allReviewCycles cache',
        });
        return cached;
      }

      const rawItems: any[] = [];
      let nextToken: string | null | undefined;
      let page = 0;
      do {
        page += 1;
        const result = await graphqlWithLog({
          query: listReviewCyclesQuery,
          variables: { limit: 100, nextToken },
          ...getAuthMode(),
        } as any);
        const data = getData(result);
        const connection = data?.listPMSReviewCycles;
        const pageItems = connection?.items || [];
        rawItems.push(...pageItems);
        nextToken = connection?.nextToken;
        console.info('[GraphQL PMS][ReviewCycles] API page', {
          page,
          pageSize: pageItems.length,
          cumulativeCount: rawItems.length,
          hasNextPage: Boolean(nextToken),
          requestLimit: 100,
        });
      } while (nextToken);

      const cycles = rawItems
        .filter((item: any) => item && item.id)
        .map((item: any) => {
          try { return mapReviewCycle(item); } catch { return null; }
        })
        .filter(Boolean) as ReviewCycle[];
      console.info('[GraphQL PMS][ReviewCycles] Full list complete', {
        totalReviewCyclesInDatabase: cycles.length,
        cyclesReturnedByApi: cycles.length,
        pagesLoaded: page,
        paginationApplied: page > 1,
        requestLimitPerPage: 100,
        slicingApplied: false,
      });
      setCachedCycles(cacheKey, cycles);
      return cycles;
    } catch (error: any) {
      // Recover partial data from AppSync partial errors
      const partial = error?.data?.listPMSReviewCycles?.items;
      if (partial && Array.isArray(partial)) {
        console.warn('[GraphQL PMS] getAllReviewCycles partial recovery:', partial.length, 'items');
        return partial
          .filter((item: any) => item && item.id)
          .map((item: any) => {
            try { return mapReviewCycle(item); } catch { return null; }
          })
          .filter(Boolean) as ReviewCycle[];
      }
      console.error('[GraphQL PMS] getAllReviewCycles error:', error);
      return [];
    }
  }

  static async getActiveReviewCycle(): Promise<ReviewCycle | undefined> {
    try {
      const result = await graphqlWithLog({
        query: reviewCyclesByStatusQuery,
        variables: { status: ReviewCycleStatus.ACTIVE, limit: 1 },
        ...getAuthMode(),
      } as any);
      const data = getData(result);
      const items = (data?.pmsReviewCyclesByStatus?.items || []).map(mapReviewCycle);
      return items[0];
    } catch (error) {
      console.error('[GraphQL PMS] getActiveReviewCycle error:', error);
      return undefined;
    }
  }

  static async deleteReviewCycle(cycleId: string): Promise<{ success: boolean; errors: string[] }> {
    try {
      await graphqlWithLog({
        query: deleteReviewCycleMutation,
        variables: { id: cycleId },
        ...getAuthMode(),
      } as any);
      clearPmsReadCaches();
      return { success: true, errors: [] };
    } catch (error: any) {
      console.error('[GraphQL PMS] deleteReviewCycle error:', error);
      return { success: false, errors: [error?.errors?.[0]?.message || error.message || 'Failed to delete review cycle'] };
    }
  }

  // ---- Annual Cycle vs Review Cycle Helpers ----

  /** Get all annual objective cycles (records with no annualCycleId) */
  static async getAnnualCycles(): Promise<ReviewCycle[]> {
    const all = await GraphQLPMSService.getAllReviewCycles();
    return all.filter(c => !c.annualCycleId);
  }

  /** Get review cycles that belong to a specific annual cycle */
  static async getReviewCyclesByAnnualCycle(annualCycleId: string): Promise<ReviewCycle[]> {
    const all = await GraphQLPMSService.getAllReviewCycles();
    return all.filter(c => c.annualCycleId === annualCycleId);
  }

  /** Get the active annual cycle (parent cycle with Active status and no annualCycleId) */
  static async getActiveAnnualCycle(): Promise<ReviewCycle | undefined> {
    const all = await GraphQLPMSService.getAllReviewCycles();
    return all.find(c => !c.annualCycleId && c.status === ReviewCycleStatus.ACTIVE);
  }

  /** Get all published (Active) review cycles across all annual cycles */
  static async getPublishedReviewCycles(): Promise<ReviewCycle[]> {
    const all = await GraphQLPMSService.getAllReviewCycles();
    return all.filter(c => c.annualCycleId && c.status === ReviewCycleStatus.ACTIVE);
  }

  // ---- Chat Operations ----

  static async sendMessage(
    input: CreateChatMessageInput,
    senderId: string,
    senderName: string
  ): Promise<PMSServiceResult<ChatMessage>> {
    try {
      const gqlInput: any = {
        threadType: input.threadType,
        threadId: input.threadId,
        content: input.content,
        senderId,
        senderName,
      };

      const result = await graphqlWithLog({
        query: createChatMessageMutation,
        variables: { input: gqlInput },
        ...getAuthMode(),
      } as any);

      const data = getData(result);
      const message = mapChatMessage(data?.createPMSChatMessage);
      return { success: true, entity: message, errors: [] };
    } catch (error: any) {
      console.error('[GraphQL PMS] sendMessage error:', error);
      return { success: false, errors: [error?.errors?.[0]?.message || error.message || 'Failed to send message'] };
    }
  }

  static async getMessages(threadType: ChatThreadType, threadId: string): Promise<ChatMessage[]> {
    try {
      const result = await graphqlWithLog({
        query: chatMessagesByThreadQuery,
        variables: { threadId, limit: 100 },
        ...getAuthMode(),
      } as any);
      const data = getData(result);
      const messages = (data?.pmsChatMessagesByThread?.items || []).map(mapChatMessage);
      logPmsRead({
        operation: 'pmsChatMessagesByThread',
        table: PMS_TABLES.messages,
        reviewId: threadId,
        recordCount: messages.length,
      });
      return messages;
    } catch (error) {
      console.error('[GraphQL PMS] getMessages error:', error);
      return [];
    }
  }

  // ---- Attachment Operations ----

  static async addAttachment(
    input: CreateAttachmentInput,
    uploadedBy: string
  ): Promise<PMSServiceResult<Attachment>> {
    try {
      const gqlInput: any = { ...input, uploadedBy };

      const result = await graphqlWithLog({
        query: createAttachmentMutation,
        variables: { input: gqlInput },
        ...getAuthMode(),
      } as any);

      const data = getData(result);
      const attachment = mapAttachment(data?.createPMSAttachment);
      return { success: true, entity: attachment, errors: [] };
    } catch (error: any) {
      console.error('[GraphQL PMS] addAttachment error:', error);
      return { success: false, errors: [error?.errors?.[0]?.message || error.message || 'Failed to add attachment'] };
    }
  }

  static async deleteAttachment(attachmentId: string): Promise<{ success: boolean; errors: string[] }> {
    try {
      await graphqlWithLog({
        query: deleteAttachmentMutation,
        variables: { id: attachmentId },
        ...getAuthMode(),
      } as any);
      return { success: true, errors: [] };
    } catch (error: any) {
      console.error('[GraphQL PMS] deleteAttachment error:', error);
      return { success: false, errors: [error?.errors?.[0]?.message || error.message || 'Failed to delete attachment'] };
    }
  }

  static async getAttachments(parentType: AttachmentParentType, parentId: string): Promise<Attachment[]> {
    try {
      const startedAt = performance.now();
      const result = await graphqlWithLog({
        query: attachmentsByParentQuery,
        variables: { parentId, limit: 100 },
        ...getAuthMode(),
      } as any);
      const data = getData(result);
      const rawItems = data?.pmsAttachmentsByParent?.items || [];
      const mappingStartedAt = performance.now();
      const attachments = rawItems.map(mapAttachment);
      console.log('[GraphQL PMS][Profile] getAttachments', {
        fetchDataMs: Math.round(mappingStartedAt - startedAt),
        mappingMs: Math.round(performance.now() - mappingStartedAt),
        parentType,
        parentId,
        rawCount: rawItems.length,
        returnedCount: attachments.length,
        s3Lookups: 0,
      });
      logPmsRead({
        operation: 'pmsAttachmentsByParent',
        table: PMS_TABLES.attachments,
        reviewId: parentId,
        recordCount: attachments.length,
      });
      return attachments;
    } catch (error) {
      console.error('[GraphQL PMS] getAttachments error:', error);
      return [];
    }
  }

  // ---- Version History ----

  static async getObjectiveVersions(objectiveId: string): Promise<ObjectiveVersion[]> {
    try {
      const startedAt = performance.now();
      const result = await graphqlWithLog({
        query: objectiveVersionsByObjectiveQuery,
        variables: { objectiveId, limit: 100 },
        ...getAuthMode(),
      } as any);
      const data = getData(result);
      const rawItems = data?.pmsObjectiveVersionsByObjective?.items || [];
      const mappingStartedAt = performance.now();
      const versions = rawItems.map(mapObjectiveVersion);
      console.log('[GraphQL PMS][Profile] getObjectiveVersions', {
        fetchDataMs: Math.round(mappingStartedAt - startedAt),
        mappingMs: Math.round(performance.now() - mappingStartedAt),
        objectiveId,
        rawCount: rawItems.length,
        returnedCount: versions.length,
      });
      return versions;
    } catch (error) {
      console.error('[GraphQL PMS] getObjectiveVersions error:', error);
      return [];
    }
  }

  private static inferObjectiveAction(updates: UpdateObjectiveInput): string {
    if (updates.status === ObjectiveStatus.SUBMITTED) return 'Submitted';
    if (updates.status === ObjectiveStatus.APPROVED) return 'Approved';
    if (updates.status === ObjectiveStatus.RETURNED) return 'Returned';
    if (updates.status === ObjectiveStatus.COMPLETED) return 'Review Submitted';
    if (updates.progressPercentage !== undefined) return 'Progress Updated';
    return 'Objective Updated';
  }

  static async createObjectiveHistory(
    objective: Objective,
    input: {
      action: string;
      changedFields: string[];
      previousValues: Record<string, unknown>;
      newValues: Record<string, unknown>;
      changedBy: string;
      changedByName: string;
    }
  ): Promise<void> {
    try {
      await graphqlWithLog({
        query: createObjectiveVersionMutation,
        variables: {
          input: {
            objectiveId: objective.id,
            action: input.action,
            changedFields: input.changedFields,
            previousValues: JSON.stringify(input.previousValues),
            newValues: JSON.stringify(input.newValues),
            changedBy: input.changedBy,
            changedByName: input.changedByName,
            statusAtChange: objective.status,
          },
        },
        ...getAuthMode(),
      } as any);
    } catch (error) {
      console.error('[GraphQL PMS] createObjectiveHistory error:', error);
    }
  }

  // ---- Lock Operations ----

  static async lockObjectivesForCycle(
    reviewCycleId: string
  ): Promise<{ success: boolean; errors: string[] }> {
    try {
      await graphqlWithLog({
        query: lockObjectivesMutation,
        variables: { reviewCycleId },
        ...getAuthMode(),
      } as any);
      return { success: true, errors: [] };
    } catch (error: any) {
      console.error('[GraphQL PMS] lockObjectivesForCycle error:', error);
      return { success: false, errors: [error?.errors?.[0]?.message || error.message || 'Failed to lock objectives'] };
    }
  }

  static async unlockObjectivesForCycle(
    reviewCycleId: string
  ): Promise<{ success: boolean; errors: string[] }> {
    try {
      await graphqlWithLog({
        query: unlockObjectivesMutation,
        variables: { reviewCycleId },
        ...getAuthMode(),
      } as any);
      return { success: true, errors: [] };
    } catch (error: any) {
      console.error('[GraphQL PMS] unlockObjectivesForCycle error:', error);
      return { success: false, errors: [error?.errors?.[0]?.message || error.message || 'Failed to unlock objectives'] };
    }
  }

  // ---- Review Phase Operations (HR-controlled open/close of a Mid-Year/Year-End
  // child cycle's review phase — deliberately separate from lock/unlock above,
  // which govern the annual cycle's #status, not this cycle's #reviewPhase) ----

  static async openReviewCycle(
    reviewCycleId: string
  ): Promise<PMSServiceResult<ReviewCycle>> {
    try {
      const result = await graphqlWithLog({
        query: openReviewCycleMutation,
        variables: { reviewCycleId },
        ...getAuthMode(),
      } as any);
      const data = getData(result);
      const cycle = mapReviewCycle(data?.openReviewCycle);
      clearPmsReadCaches();
      return { success: true, entity: cycle, errors: [] };
    } catch (error: any) {
      console.error('[GraphQL PMS] openReviewCycle error:', error);
      return { success: false, errors: [error?.errors?.[0]?.message || error.message || 'Failed to open review'] };
    }
  }

  static async closeReviewCycle(
    reviewCycleId: string
  ): Promise<PMSServiceResult<ReviewCycle>> {
    try {
      const result = await graphqlWithLog({
        query: closeReviewCycleMutation,
        variables: { reviewCycleId },
        ...getAuthMode(),
      } as any);
      const data = getData(result);
      const cycle = mapReviewCycle(data?.closeReviewCycle);
      clearPmsReadCaches();
      return { success: true, entity: cycle, errors: [] };
    } catch (error: any) {
      console.error('[GraphQL PMS] closeReviewCycle error:', error);
      return { success: false, errors: [error?.errors?.[0]?.message || error.message || 'Failed to close review'] };
    }
  }

  // ---- Team Members (for Manager Dashboard filter) ----

  /** Get team members reporting to a manager by querying the Users table */
  static async getTeamMembers(managerEmail: string): Promise<{ id: string; email: string; name: string }[]> {
    try {
      const listUsersQuery = `query ListUsers($limit: Int) { listUsers(limit: $limit) { items { id email name managerEmail } } }`;
      const result = await graphqlWithLog({
        query: listUsersQuery,
        variables: { limit: 200 },
        ...getAuthMode(),
      } as any);
      const data = getData(result);
      const allUsers = data?.listUsers?.items || [];
      return allUsers
        .filter((u: any) => sameIdentity(u.managerEmail, managerEmail))
        .map((u: any) => ({ id: u.id || '', email: u.email || '', name: u.name || '' }));
    } catch (error) {
      console.error('[GraphQL PMS] getTeamMembers error:', error);
      return [];
    }
  }
}
